import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { buildMyClassesQuery, getDocsByClassIds, schoolYearQuarterLabels } from '../utils/classroomQueries';
import { usePageLoad } from '../hooks/usePageLoad';
import LoadErrorNotice from '../components/LoadErrorNotice';
import StudentDetailsModal from '../components/StudentDetailsModal';
import { readinessNotesFromDocs } from '../utils/studentNotes';
import AssessmentExportControls from '../components/AssessmentExportControls';
import ReportCardNarrativeModal from '../components/ReportCardNarrativeModal';
import ReportCardPrintView from '../components/ReportCardPrintView';
import SavedReportCardsSection from '../components/SavedReportCardsSection';
import { lastNameComparator } from '../utils/sortByLastName';
import { loadRemoteFeatureFlags } from '../utils/remoteFeatureFlags';
import { currentSchoolYear, formatPeriodLabel, sortDraftsByGeneratedDesc } from '../utils/narrativeDrafts';
import { buildResponseCellTooltip } from '../utils/incorrectResponses';
import { isDocInSchoolYear, isQuarterInSchoolYear, schoolYearStartYear, schoolYearLabel } from '../utils/schoolYear';
import { resolveActiveSchoolYear, getCalendarQuarters } from '../utils/activeSchoolYear';
import { formatLetterForAssessment, letterCaseForSelection, letterCaseOf, hasLetterCase } from '../utils/letterCase';
import { caseTagText, caseTagTitle, LETTER_CASES } from '../utils/assessmentEntry';
import { LETTER_MAX } from '../utils/bandForScore';
import SchoolYearNotice from '../components/SchoolYearNotice';
import NoRosterNotice from '../components/NoRosterNotice';
import { isFeatureEnabled } from '../constants/featureFlags';
import { trackWrite } from '../utils/pendingWrites';
import { isNewerAttempt, latestAttemptIn, isNaAttempt } from '../utils/latestAttempt';
import { auditResultChange, resultSnapshot, RESULT_AUDIT_ACTIONS } from '../utils/resultAudit';
import { buildClassLabelMap, classLabelFrom } from '../utils/classDisplay';
import { useOnboardingScreen, TOUR_TARGETS, HINT_KEYS } from '../onboarding';
import {
  VOWEL_ITEMS_PER_TEST,
  VOWEL_TEST_LABELS,
  VOWEL_TEST_SHORT_LABELS,
  RESP_NOT_ADMINISTERED,
  isVowelTestType,
  vowelItemIds,
  vowelScoreColorClass,
  enabledVowelTestTypes,
  enabledVowelTestLabels,
} from '../constants/vowelTests';

const LETTER_ASSESSMENT_LABELS = {
  uppercase: 'Uppercase Letters',
  lowercase: 'Lowercase Letters',
  sounds: 'Letter Sounds',
};

// This page only shows letter-knowledge tests. NWF (and legacy first_sound)
// docs share the assessments collection but belong to the Nonsense Word
// Fluency pages — mirror of PA_TEST_TYPES in BenchmarkScreenerResults.js.
// Phonemic awareness is assessed in the standalone Phonemic Awareness
// screener; the legacy manual-entry Q4 phonemic column was retired in
// v3.66.0 after an audit found zero recorded scores.
//
// v3.73.0: the two optional vowel tests (long_vowels / short_vowels) join
// this choke point ONLY while the vowelTests kill switch is on — see
// letterTestTypes() below. With the flag off their docs are filtered out here
// and no downstream surface (summary grid, detail tables, modal, CSV) sees
// them.
const LETTER_TEST_TYPES = Object.keys(LETTER_ASSESSMENT_LABELS);
const letterAssessmentLabels = () => ({ ...LETTER_ASSESSMENT_LABELS, ...enabledVowelTestLabels() });
const letterTestTypes = () => [...LETTER_TEST_TYPES, ...enabledVowelTestTypes()];

// Hard cap for the hidden double-click manual entry: 26 letters for the three
// letter tests, 5 items for a vowel test. Overriding a saved result caps at
// the items that result asked (v3.98.1): a consonants-only Sounds run asked
// 21, so a typed 24 would read 24/21.
const manualScoreMax = (testType, existing = null) => {
  const cap = isVowelTestType(testType) ? VOWEL_ITEMS_PER_TEST : 26;
  const asked = existing && Number.isInteger(existing.totalLetters) && existing.totalLetters > 0
    ? existing.totalLetters : cap;
  return Math.min(cap, asked);
};

// Which attempt a summary cell shows, and which one the double-click edit
// acts on: src/utils/latestAttempt.js (v3.92.2).

// Said letter inside a red Detailed Responses cell — display only, all five
// Letter/Sound tests. The letter the teacher recorded through a letter key
// (incorrectResponses[itemKey], the v3.27.0 map) renders small inside the
// incorrect cell; the target letter stays in the column header and the cell
// tooltip is unchanged. Bold near-black rather than white: it reads on the red
// fill (≈5.7:1) and on plain white if a printer drops backgrounds — the Results
// print overlay reuses this DOM. 0.875em is 14px at the page base, so the
// phone audit's small-text check stays quiet. Red cells with no recorded
// letter (explicit ←, manual entry, pre-v3.27.0 docs), green and N/A cells
// render exactly as before.
const SAID_LETTER_STYLE = { fontSize: '0.875em', fontWeight: 700, color: '#111827' };

// The small case tag beside a Letter/Sound score (v3.85.0): Aa / aa / A a —
// rendered only for docs that carry `letterCase` (every doc written by a
// Sounds variant or a vowel run with the variants flag on); older docs show
// no tag, so the table is byte-identical for them.
const CASE_TAG_STYLE = { marginLeft: '4px', padding: '0 4px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: '0.7em', fontWeight: 600, verticalAlign: 'middle', whiteSpace: 'nowrap' };
// Not-administered letters in the a–z Detailed Letter Responses grid (v3.85.0):
// the vowel grid's grey "n/a" cell, reused — never counted, never red.
const NOT_ADMINISTERED_CELL_STYLE = { backgroundColor: '#e5e7eb', color: '#6b7280', fontSize: '0.8em' };

// Header-cell filter select of the Detailed Vowel Responses block — the same
// properties the a–z grid's three inline selects use, so both tables' filters
// look and behave alike (the mobile select restyle in App.css applies to both).
const DETAIL_FILTER_SELECT_STYLE = {
  padding: '4px',
  borderRadius: '4px',
  border: 'none',
  backgroundColor: 'transparent',
  fontWeight: 'bold',
  fontSize: '1em',
  width: '100%',
  cursor: 'pointer',
  appearance: 'auto',
};

function Results({ user }) {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  // 4K Readiness notes by student (v3.96.0), from the same fetch — the
  // Letter/Sound list below drops those docs, the student pop-up shows them.
  const [readinessNotesByStudent, setReadinessNotesByStudent] = useState({});
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState({});
  const [schoolYearInfo, setSchoolYearInfo] = useState(null);
  // The resolved active school-year start year, readable inside fetch closures.
  const activeYearRef = React.useRef(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalData, setModalData] = useState({ student: null, assessments: [] });
  const [quarters, setQuarters] = useState({});
  const [testTypeFilters, setTestTypeFilters] = useState({});
  const [quarterFilters, setQuarterFilters] = useState({});
  const [studentFilters, setStudentFilters] = useState({});
  // Detailed Vowel Responses filters (PR #111): the vowel block filters
  // itself — Student / Test / Quarter of its own, keyed by classId like the
  // three above — independently of the a–z grid.
  const [vowelTestTypeFilters, setVowelTestTypeFilters] = useState({});
  const [vowelQuarterFilters, setVowelQuarterFilters] = useState({});
  const [vowelStudentFilters, setVowelStudentFilters] = useState({});
  const [selectedClassFilter, setSelectedClassFilter] = useState('All');
  // Names for every student doc we downloaded, BEFORE the school-year archive
  // filter — lets the detailed table label an archived student's results with
  // their real name instead of "Unknown". (Hard-deleted students have no doc
  // at all; their results fall back to "Removed student".)
  const [studentNamesById, setStudentNamesById] = useState({});
  // Per-class toggle: results whose student is no longer on the roster are
  // hidden by default so the Incorrect Count row reflects the current class;
  // the checkbox above the detailed table reveals them.
  const [removedShown, setRemovedShown] = useState({});
  // Per-class toggle: when a student was assessed more than once for the same
  // test + quarter, the detailed table shows only the latest attempt by
  // default (matching the summary table's pick); the checkbox reveals all.
  const [allAttemptsShown, setAllAttemptsShown] = useState({});
  // School-year scoping: teacher pages show the ACTIVE school year only,
  // resolved from the Academic Calendar (v3.29.0 — district-specific dates
  // first). Previous years' classes/students/results are archived — they stay
  // in Firestore but are never loaded into the UI.
  const selectedSchoolYear = schoolYearInfo?.status === 'active' ? schoolYearInfo.startYear : null;
  // AI report-card narrative gating (appConfig/featureFlags). The general
  // `aiNarratives` flag shows the feature to teachers + admins; the parallel
  // `aiNarrativesAdminPreview` flag shows it to admins only, for QA before the
  // general launch. Both default off → button hidden for everyone.
  const [aiNarrativesEnabled, setAiNarrativesEnabled] = useState(false);
  const [aiNarrativesAdminPreview, setAiNarrativesAdminPreview] = useState(false);
  const [narrativeStudent, setNarrativeStudent] = useState(null);
  // CSV export panel — parked pending SIS-export work (see BACKLOG.md).
  const [assessmentCsvExportEnabled, setAssessmentCsvExportEnabled] = useState(false);

  // "Saved Report Cards" class-level overview (read/view/print/open only).
  const [savedCardsExpanded, setSavedCardsExpanded] = useState(false);
  const [ovReportType, setOvReportType] = useState('quarterly');
  const [ovQuarter, setOvQuarter] = useState(2);
  const [ovSemester, setOvSemester] = useState('fall');
  const [ovSchoolYear, setOvSchoolYear] = useState(currentSchoolYear());
  // Cache of narrativeDrafts per studentId (loaded lazily, one getDocs per
  // student — the same per-student list read the modal already uses, which the
  // rules authorize without a collectionGroup query). draftsLoadedFor tracks
  // which students have been fetched so we don't refetch on every render.
  const [draftsByStudent, setDraftsByStudent] = useState({});
  const [draftsLoadedFor, setDraftsLoadedFor] = useState({});
  const [draftsLoading, setDraftsLoading] = useState(false);
  // Parent-only print view (never carries teacher flags — see ReportCardPrintView).
  const [printDraft, setPrintDraft] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadRemoteFeatureFlags().then((flags) => {
      if (cancelled) return;
      setAiNarrativesEnabled(flags.aiNarratives === true);
      setAiNarrativesAdminPreview(flags.aiNarrativesAdminPreview === true);
      setAssessmentCsvExportEnabled(flags.assessmentCsvExport === true);
    });
    return () => { cancelled = true; };
  }, []);

  const isAdmin = !!user?.role?.admin;
  const canUseNarratives =
    (aiNarrativesEnabled && (user?.role?.teacher || isAdmin))
    || (aiNarrativesAdminPreview && isAdmin);

  // Load narrativeDrafts for the given students (one per-student getDocs each,
  // matching the modal's proven query shape). Skips any already cached unless
  // `force` is set (used to refresh after the modal closes so status updates).
  const loadDraftsForStudents = useCallback(async (studentIds, { force = false } = {}) => {
    const idsToLoad = studentIds.filter((id) => force || !draftsLoadedFor[id]);
    if (idsToLoad.length === 0) return;
    setDraftsLoading(true);
    try {
      const results = await Promise.all(idsToLoad.map(async (id) => {
        try {
          const snap = await getDocs(collection(db, 'students', id, 'narrativeDrafts'));
          return [id, sortDraftsByGeneratedDesc(snap.docs.map((d) => ({ id: d.id, ...d.data() })))];
        } catch (err) {
          console.error('Results: failed to load narrativeDrafts for', id, err);
          return [id, []];
        }
      }));
      setDraftsByStudent((prev) => {
        const next = { ...prev };
        results.forEach(([id, drafts]) => { next[id] = drafts; });
        return next;
      });
      setDraftsLoadedFor((prev) => {
        const next = { ...prev };
        idsToLoad.forEach((id) => { next[id] = true; });
        return next;
      });
    } finally {
      setDraftsLoading(false);
    }
  }, [draftsLoadedFor]);

  const fetchData = useCallback(async () => {
    {
      // Which school year is active? Calendar-driven (district-specific first).
      const sy = await resolveActiveSchoolYear(user?.profile?.districtId);
      setSchoolYearInfo(sy);
      activeYearRef.current = sy.status === 'active' ? sy.startYear : null;
      if (sy.status !== 'active') return;

      // Role-aware query: pure-EA tokens use the assistantEmails clause only
      // (the or() shape is rules-denied without the teacher claim).
      const classesSnapshot = await getDocs(buildMyClassesQuery(user));
      const classesData = {};
      const classIds = [];
      // Current school year only — unstamped/older classes are archived.
      classesSnapshot.docs.filter(d => !d.data().deleted && isDocInSchoolYear(d.data(), activeYearRef.current)).forEach(doc => {
        classesData[doc.id] = doc.data();
        classIds.push(doc.id);
      });
      setClasses(classesData);

      // Assessments scoped to the active year's quarters SERVER-SIDE — the
      // class's prior-year history is no longer downloaded (audit step 1b).
      // Students and assessments are independent, so they go out together
      // rather than one after the other (2026-07 perf audit, step 5).
      const [studentDocs, assessmentDocs] = await Promise.all([
        getDocsByClassIds('students', classIds),
        getDocsByClassIds('assessments', classIds, {
          quarterLabels: schoolYearQuarterLabels(activeYearRef.current),
        }),
      ]);

      // Name lookup from the raw fetch (pre-archive-filter, no extra reads).
      const namesById = {};
      studentDocs.forEach(doc => { namesById[doc.id] = doc.data().name; });
      setStudentNamesById(namesById);
      const studentsList = studentDocs
        .map(doc => {
          const data = doc.data();
          return { id: doc.id, ...data, originalClassId: data.classId ? data.classId.split('_')[0] : 'Unknown' };
        })
        .filter(s => isDocInSchoolYear(s, activeYearRef.current)); // archived students stay hidden even if their class doc was re-stamped

      setReadinessNotesByStudent(readinessNotesFromDocs(assessmentDocs));

      let assessmentsList = assessmentDocs.map(doc => {
        const data = doc.data();
        if (data.timestamp && typeof data.timestamp.toDate === 'function') {
          data.timestamp = data.timestamp.toDate();
        }
        return { id: doc.id, ...data };
      });

      const visibleTestTypes = letterTestTypes();
      assessmentsList = assessmentsList.filter(a =>
        a.quarter && a.quarter.includes('Q') && visibleTestTypes.includes(a.testType)
      );
      setStudents(studentsList);
      setAssessments(assessmentsList);

      // Academic Calendar (default overlaid by the district's). Served from
      // the session cache resolveActiveSchoolYear() already populated, so this
      // costs no round trip and re-reads nothing (2026-07 perf audit, step 5).
      setQuarters(await getCalendarQuarters(user?.profile?.districtId || null));
    }
  }, [user]);

  // Shared load state: rules denials / missing indexes / offline surface as a
  // retryable notice instead of a silent empty page; hung loads error at 15s.
  const { loading, error: loadError, retry: retryLoad } = usePageLoad(fetchData, [fetchData], { enabled: !!user });

  useEffect(() => {
    document.title = "Early Literacy Assessment - Assessment Results";
  }, []);

  // Lazily load drafts for the students visible in the overview, once it's
  // expanded. Re-runs when the class filter changes (loads any newly-visible
  // students); already-cached students are skipped inside loadDraftsForStudents.
  useEffect(() => {
    if (!savedCardsExpanded || !canUseNarratives) return;
    const visibleStudentIds = students
      .filter((s) => s.classId && classes[s.classId]
        && (selectedClassFilter === 'All' || s.classId === selectedClassFilter))
      .map((s) => s.id);
    if (visibleStudentIds.length > 0) {
      loadDraftsForStudents(visibleStudentIds);
    }
  }, [savedCardsExpanded, canUseNarratives, students, classes, selectedClassFilter, loadDraftsForStudents]);

  // Overview actions. View/Generate reuse the existing single-student modal;
  // View passes the resolved draft id so the modal opens that exact draft.
  const overviewPeriod = { reportType: ovReportType, quarterNumber: ovQuarter, semester: ovSemester, schoolYear: ovSchoolYear };

  const handleOverviewView = useCallback((stu, draft) => {
    setNarrativeStudent({
      studentId: stu.studentId,
      name: stu.name,
      classId: stu.classId,
      initial: overviewPeriod,
      autoOpenDraftId: draft.id,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovReportType, ovQuarter, ovSemester, ovSchoolYear]);

  const handleOverviewGenerate = useCallback((stu) => {
    setNarrativeStudent({
      studentId: stu.studentId,
      name: stu.name,
      classId: stu.classId,
      initial: overviewPeriod,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovReportType, ovQuarter, ovSemester, ovSchoolYear]);

  const handleOverviewPrint = useCallback((stu, draft, originalClassId) => {
    // Parent-only: print uses the edited text if present, else the AI text. No
    // draft object, feedback, or teacher flags are passed to the print view.
    setPrintDraft({
      studentName: stu.name,
      studentClass: originalClassId,
      schoolYear: draft.schoolYear || ovSchoolYear,
      periodLabel: draft.periodLabel || formatPeriodLabel(overviewPeriod),
      narrativeText: (draft.finalEditedText ?? draft.narrative) || '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovReportType, ovQuarter, ovSemester, ovSchoolYear]);

  // Current-roster ids — assessments whose student no longer resolves here
  // belong to removed (hard-deleted) or archived students.
  const rosterIds = React.useMemo(() => new Set(students.map(s => s.id)), [students]);

  // Ids of the latest attempt per student/testType/quarter among displayable
  // (non-N/A) records — same comparator as the summary table's dedupe. Used
  // by both the detailed table and the CSV export so they can never disagree.
  const latestAttemptIds = React.useMemo(() => {
    const latestByKey = {};
    assessments.forEach(a => {
      if (a.correctCount === -1 || a.correctCount === 'N/A') return;
      const key = `${a.studentId}-${a.testType}-${a.quarter}`;
      if (!latestByKey[key] || (a.timestamp && new Date(a.timestamp) > new Date(latestByKey[key].timestamp))) {
        latestByKey[key] = a;
      }
    });
    return new Set(Object.values(latestByKey).map(a => a.id));
  }, [assessments]);

  const filterAssessmentForExport = useCallback((assessment) => {
    const classId = assessment.classId;
    // Vowel docs mirror the Detailed Vowel Responses block's own filters;
    // letter docs mirror the a–z grid's (PR #111).
    const vowel = isVowelTestType(assessment.testType);
    const currentTestTypeFilter = (vowel ? vowelTestTypeFilters : testTypeFilters)[classId] || 'All';
    const currentQuarterFilter = (vowel ? vowelQuarterFilters : quarterFilters)[classId] || 'All';
    const currentStudentFilter = (vowel ? vowelStudentFilters : studentFilters)[classId] || 'All';
    const matchTestType = currentTestTypeFilter === 'All' || assessment.testType === currentTestTypeFilter;
    const matchQuarter = currentQuarterFilter === 'All' || assessment.quarter === currentQuarterFilter;
    const matchStudent = currentStudentFilter === 'All' || assessment.studentId === currentStudentFilter;
    // Mirror the detailed table: removed students export only when revealed.
    const matchRoster = rosterIds.has(assessment.studentId) || !!removedShown[classId];
    // Mirror the detailed table: older duplicate attempts export only when
    // revealed. (N/A records pass — they are not "attempts" in the table.)
    const isNA = assessment.correctCount === -1 || assessment.correctCount === 'N/A';
    const matchAttempt = isNA || latestAttemptIds.has(assessment.id) || !!allAttemptsShown[classId];
    return matchTestType && matchQuarter && matchStudent && matchRoster && matchAttempt;
  }, [quarterFilters, studentFilters, testTypeFilters, vowelQuarterFilters, vowelStudentFilters, vowelTestTypeFilters, rosterIds, removedShown, latestAttemptIds, allAttemptsShown]);

  const getCorrectCount = (correctCount) => {
    return correctCount || 0;
  };

  // Results' own colour cuts — ≥ 21 / ≥ 14 on a 26-letter run, unchanged
  // since the page began (the BAND CUT POINTS decision is still open). v3.85.0:
  // expressed as fractions of 26 so a 21-letter consonants-only run colours
  // on its own scale (≥ 17 / ≥ 12); integer maths, and identical on 26.
  const scaledResultsCut = (cut, total) => Math.floor((cut * total + (LETTER_MAX - 1)) / LETTER_MAX);
  const getScoreColorClass = (score, total = LETTER_MAX) => {
    if (score === '' || score === null || score === undefined) return '';
    const numericScore = Number(score);
    if (numericScore === -1) return 'score-na';  // Grayed out
    const n = Number.isFinite(Number(total)) && Number(total) > 0 ? Math.floor(Number(total)) : LETTER_MAX;
    if (numericScore >= scaledResultsCut(21, n)) return 'score-high'; // Approx 80%+
    if (numericScore >= scaledResultsCut(14, n)) return 'score-mid';  // Approx 50-80%
    if (numericScore >= 0) return 'score-low';   // Below 50%
    return '';
  };

  // A summary cell's content (v3.85.0): the score as before; "x/N" only when
  // the run's total is not 26 (a consonants-only run), and the small case
  // tag only when the doc carries letterCase — every older doc renders
  // exactly as it did.
  const letterCellContent = (summary, key) => {
    const score = summary[key];
    if (score === '' || score === null || score === undefined) return '';
    if (score === -1) return '';
    const total = summary[`${key}_total`];
    const letterCase = summary[`${key}_case`];
    const shortRun = Number.isFinite(Number(total)) && Number(total) > 0 && Number(total) !== LETTER_MAX;
    const text = shortRun ? `${score}/${total}` : score;
    if (!letterCase) return text;
    return (
      <>
        {text}
        <span className="case-tag" title={caseTagTitle(letterCase)} style={CASE_TAG_STYLE}>{caseTagText(letterCase)}</span>
      </>
    );
  };

  const displayScore = (score) => {
    return score === -1 ? '' : score;
  };


  const getCurrentQuarter = () => {
    const assessmentDate = new Date();
    const currentYear = assessmentDate.getFullYear();
    const yearsToCheck = [currentYear.toString(), (currentYear - 1).toString()];

    for (const year of yearsToCheck) {
      const yearQuarters = quarters[year] || {};
      for (const quarter in yearQuarters) {
        const { start, end } = yearQuarters[quarter];
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (assessmentDate >= startDate && assessmentDate <= endDate) {
          return `${year}Q${quarter}`;
        }
      }
    }
    // Fallback when no calendar range matches: use the SCHOOL-year start year
    // (July 1 rule), not the calendar year — a bare calendar year mislabels
    // Jan–Jun dates into the next school year.
    return `${activeYearRef.current ?? schoolYearStartYear(assessmentDate)}Q1`;
  };

  const handleManualScore = async (studentId, classId, testType, year, quarter, currentScore) => {
    // 1. Check if cell is empty (score is missing)
    let isOverwrite = false;
    let existingAssessmentId = null;
    let overwrittenRecord = null;

    if (currentScore !== '' && currentScore !== undefined && currentScore !== null) {
      if (currentScore === -1 || currentScore === 'N/A') {
        const confirmRemove = window.confirm("This assessment is marked as N/A.\n\nDo you want to clear this N/A and revert to an untested state?");
        if (confirmRemove) {
          const targetQuarterStr = `${year}${quarter}`;
          // The N/A the cell shows — the latest attempt. Only an N/A doc is
          // ever deleted here; an older scored attempt in the same slot stays
          // (and shows again once the N/A is gone).
          const shown = latestAttemptIn(assessments, studentId, testType, targetQuarterStr);
          const existingRecord = isNaAttempt(shown) ? shown : null;
          if (!existingRecord) {
            console.warn('Clear N/A: the latest attempt in this slot is not an N/A doc; nothing deleted.');
          }
          if (existingRecord) {
            try {
              await deleteDoc(doc(db, 'assessments', existingRecord.id));
              setAssessments(prev => prev.filter(a => a.id !== existingRecord.id));
              await auditResultChange(user, RESULT_AUDIT_ACTIONS.NA_CLEARED, {
                id: existingRecord.id, studentId, classId: existingRecord.classId || classId, testType, quarter: targetQuarterStr,
              }, { before: resultSnapshot(existingRecord), after: null });
            } catch (err) {
              console.error("Error deleting N/A record:", err);
              alert("Failed to clear N/A status.");
            }
          }
        }
        return;
      }
      const confirmOverwrite = window.confirm(`This assessment already has a score of ${currentScore}.\n\nDo you want to override it to "NA"?`);
      if (!confirmOverwrite) return;

      isOverwrite = true;
      // We need the assessment ID to update it: the attempt the cell shows
      // (the latest), never an older attempt in the same slot.
      const targetQuarterStr = `${year}${quarter}`;
      const existingRecord = latestAttemptIn(assessments, studentId, testType, targetQuarterStr);
      if (existingRecord) {
        existingAssessmentId = existingRecord.id;
        overwrittenRecord = existingRecord;
      } else {
        console.warn("Could not find existing assessment ID locally despite having a score. Creating new record instead.");
        isOverwrite = false;
      }
    }

    const targetQuarter = `${year}${quarter}`;
    const currentQ = getCurrentQuarter();

    // 2. Check quarter — block only future quarters (current quarter is allowed for N/A and scores)
    if (targetQuarter > currentQ) {
      alert("You can only enter scores for past or current quarters.");
      return;
    }

    // 3. Prompt for score (0–26 for the letter tests; 0–5 for a vowel test;
    // an override caps at the items the saved result asked — v3.98.1)
    const maxScore = manualScoreMax(testType, overwrittenRecord);
    const scoreInput = window.prompt(`Enter manual score for ${targetQuarter} (${testType}):\n(0-${maxScore})\nEnter "NA" if the student joined late or was not tested.`);
    if (scoreInput === null) return; // Cancelled

    let score;
    if (scoreInput.trim().toUpperCase() === 'NA' || scoreInput.trim().toUpperCase() === 'N/A') {
      score = -1;
    } else {
      score = parseInt(scoreInput, 10);
      if (isNaN(score) || score < 0 || score > maxScore) {
        alert(`Invalid score. Please enter a number between 0 and ${maxScore}, or NA.`);
        return;
      }
    }

    try {
      // 4. Save to Firestore
      if (isOverwrite && existingAssessmentId) {
        const assessmentRef = doc(db, 'assessments', existingAssessmentId);
        // Do NOT rewrite teacherId: it is a rules-protected field (ownership
        // is immutable), so overwriting an EA's or co-teacher's record used to
        // fail with permission-denied (2026-07 audit, 3b#10). The class owner
        // is now authorized to update the record while the original assessor
        // stays on it.
        // Do NOT rewrite timestamp either (v3.98.1, Kemal 2026-11-02: a fix
        // never changes the test date): it is the test date the reports show,
        // and resetting it to "now" could change which attempt counts as the
        // latest. The answers the override wipes are kept in the audit entry.
        const update = {
          correctCount: score,
          manualEntry: true,
          responses: {}, // clear detailed responses if previously taken naturally
        };
        await updateDoc(assessmentRef, update);
        await auditResultChange(user, RESULT_AUDIT_ACTIONS.SCORE_OVERRIDDEN, {
          id: existingAssessmentId, studentId, classId: overwrittenRecord.classId || classId, testType, quarter: targetQuarter,
        }, { before: resultSnapshot(overwrittenRecord), after: resultSnapshot({ ...overwrittenRecord, ...update }) });
      } else {
        const created = await trackWrite(addDoc(collection(db, 'assessments'), {
          studentId,
          classId,
          quarter: targetQuarter,
          testType,
          responses: {}, // No detailed responses
          timestamp: new Date(),
          totalLetters: maxScore,
          correctCount: score,
          teacherId: user.uid,
          manualEntry: true
        }));
        await auditResultChange(user, RESULT_AUDIT_ACTIONS.MANUAL_ENTRY, {
          id: created && created.id, studentId, classId, testType, quarter: targetQuarter,
        }, { before: null, after: resultSnapshot({ correctCount: score, manualEntry: true, responses: {}, totalLetters: maxScore }) });
      }

      // If teacher marked N/A, offer to bulk-mark other empty past-quarter slots for this student
      if (score === -1) {
        const testTypes = ['uppercase', 'lowercase', 'sounds'];
        const currentQ = getCurrentQuarter();
        // NOTE: `q` already includes the "Q" prefix (e.g. "Q1"); do NOT prepend
        // another one. The previous `${yr}Q${q}` produced malformed "2025QQ1"
        // strings under conditions where the filter below let them through.
        // Fixed in v3.22.3; see backlog "QA-AI-Narratives convention re-seed".
        const pastSlots = uniqueYears.flatMap(yr =>
          ['Q1', 'Q2', 'Q3', 'Q4'].map(q => ({ yr, q, quarterKey: `${yr}${q}` }))
        ).filter(s => s.quarterKey < currentQ);

        const slotsToOffer = [];
        testTypes.forEach(tt => {
          pastSlots.forEach(({ yr, q, quarterKey }) => {
            if (tt === testType && yr === year && q === quarter) return;
            const existing = assessments.find(a =>
              a.studentId === studentId && a.testType === tt && a.quarter === quarterKey
            );
            if (!existing) slotsToOffer.push({ testType: tt, quarterKey });
          });
        });

        if (slotsToOffer.length > 0) {
          const studentName = students.find(s => s.id === studentId)?.name || 'This student';
          const lines = slotsToOffer.map(s => `• ${s.testType} — ${s.quarterKey}`).join('\n');
          const shouldBulk = window.confirm(
            `"${studentName}" was marked N/A for ${testType} ${year}${quarter}.\n\nOther past quarters appear untested. Mark them as N/A too?\n\n${lines}`
          );
          if (shouldBulk) {
            // One atomic batch instead of a write per slot: a mid-loop rules
            // denial used to leave a partial set of N/A rows (audit step 2).
            const batch = writeBatch(db);
            const bulkRefs = slotsToOffer.map(slot => {
              const ref = doc(collection(db, 'assessments'));
              batch.set(ref, {
                studentId,
                classId,
                quarter: slot.quarterKey,
                testType: slot.testType,
                responses: {},
                timestamp: new Date(),
                totalLetters: 26,
                correctCount: -1,
                teacherId: user.uid,
                manualEntry: true,
              });
              return { ref, slot };
            });
            await batch.commit();
            await Promise.all(bulkRefs.map(({ ref, slot }) => auditResultChange(user, RESULT_AUDIT_ACTIONS.MANUAL_ENTRY, {
              id: ref && ref.id, studentId, classId, testType: slot.testType, quarter: slot.quarterKey,
            }, { before: null, after: resultSnapshot({ correctCount: -1, manualEntry: true, responses: {}, totalLetters: 26 }) })));
          }
        }
      }
      retryLoad();
    } catch (err) {
      console.error("Error saving manual score:", err);
      alert("Failed to save score.");
    }
  };

  // ── School-year scoping (v3.28.0) ────────────────────────────────────────
  // Classes and students are already filtered to the current school year at
  // fetch time (stamped `schoolYear` docs only). Assessments are additionally
  // filtered here by their quarter prefix ("2026Q1" → start year 2026) so a
  // re-imported roster never resurrects previous years' results.
  const yearAssessments = assessments.filter(a => isQuarterInSchoolYear(a.quarter, selectedSchoolYear));

  const classSummaries = {};
  const uniqueYears = [String(selectedSchoolYear)];
  students.forEach(student => {
    if (student.classId && classes[student.classId]) {
      if (!classSummaries[student.classId]) classSummaries[student.classId] = [];
      const studentSummary = {
        studentId: student.id,
        name: student.name,
        homeroomTeacher: classes[student.classId]?.homeroomTeacher || 'Unknown',
        classId: student.classId,
      };
      uniqueYears.forEach(year => {
        ['uppercase', 'sounds', 'lowercase', ...enabledVowelTestTypes()].forEach(testType => {
          ['Q1', 'Q2', 'Q3', 'Q4'].forEach(quarter => {
            const baseKey = `${testType}${year}${quarter}`;
            studentSummary[baseKey] = '';
            studentSummary[`${baseKey}_date`] = null;
            studentSummary[`${baseKey}_isManual`] = false;
            studentSummary[`${baseKey}_total`] = null;
            studentSummary[`${baseKey}_case`] = null;
          });
        });
      });
      classSummaries[student.classId].push(studentSummary);
    }
  });

  // Sort each class's students by last name
  Object.keys(classSummaries).forEach(classId => {
    classSummaries[classId].sort(lastNameComparator);
  });

  const studentAssessments = {};
  yearAssessments.forEach(assessment => {
    const key = `${assessment.studentId}-${assessment.testType}-${assessment.quarter}`;
    // Same rule as the double-click edit (isNewerAttempt, v3.92.2).
    if (isNewerAttempt(assessment, studentAssessments[key])) {
      studentAssessments[key] = assessment;
    }
  });

  Object.values(studentAssessments).forEach(assessment => {
    const classId = assessment.classId;
    const studentSummary = classSummaries[classId]?.find(s => s.studentId === assessment.studentId);
    if (studentSummary) {
      const correctCount = getCorrectCount(assessment.correctCount);
      const [year, quarter] = assessment.quarter.split('Q');
      const key = `${assessment.testType}${year}Q${quarter}`;
      const dateKey = `${key}_date`;
      const manualKey = `${key}_isManual`;
      if (studentSummary.hasOwnProperty(key)) {
        studentSummary[key] = correctCount;
        studentSummary[dateKey] = assessment.timestamp;
        studentSummary[manualKey] = assessment.manualEntry || false;
        // v3.85.0: the run's item total (26, or 21 for consonants only) and
        // its case, when the doc carries one — read by letterCellContent.
        studentSummary[`${key}_total`] = Number.isFinite(Number(assessment.totalLetters)) ? Number(assessment.totalLetters) : null;
        studentSummary[`${key}_case`] = hasLetterCase(assessment) ? assessment.letterCase : null;
      }
    }
  });

  // Optional vowel tests (v3.73.0): [] when the kill switch is off, so every
  // vowel header, cell, filter option and detail block below renders nothing.
  const enabledVowels = enabledVowelTestTypes();
  const QUARTER_KEYS = ['Q1', 'Q2', 'Q3', 'Q4'];

  const classKeys = Object.keys(classSummaries);

  // Contextual hint readiness (v3.75.0, overlay only): at least one class
  // table rendered. Inert unless the onboardingTour flag is on.
  useOnboardingScreen(HINT_KEYS.results, { ready: !loading && !loadError && classKeys.length > 0 });
  const visibleClassKeys = classKeys.filter(key => selectedClassFilter === 'All' || key === selectedClassFilter);
  // Display labels (v3.71.0): override → shortened Clever name → raw.
  const classLabels = buildClassLabelMap(classes);

  // Per-class student groups for the Saved Report Cards overview — same student
  // set (and last-name order) the grid above shows.
  const overviewClassGroups = visibleClassKeys.map((classId) => ({
    classId,
    originalClassId: classLabelFrom(classLabels, classId),
    students: (classSummaries[classId] || []).map((s) => ({
      studentId: s.studentId,
      name: s.name,
      classId: s.classId || classId,
    })),
  }));

  // Calendar-driven school-year gate (v3.29.0): when no school year is active
  // per the Academic Calendar, show the notice and no page content.
  if (!loading && schoolYearInfo && schoolYearInfo.status !== 'active') {
    return <SchoolYearNotice info={schoolYearInfo} />;
  }

  return (
    <div>
      {/* Wrap main dashboard content to hide it during print if a modal or the
          report-card print view is open */}
      <div className={(selectedStudent || printDraft) ? 'no-print' : ''}>
        <div className="assessment-banner no-print">
          Letter/Sound Assessment
        </div>
        <h2 style={{ textAlign: 'center', fontSize: '2em' }}>Results</h2>

	        {!loading && classKeys.length > 0 && (
	          <div style={{ textAlign: 'center', marginBottom: '20px' }} className="no-print">
            <label htmlFor="class-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>Select Class:</label>
            <select
              id="class-select"
              value={selectedClassFilter}
              onChange={(e) => setSelectedClassFilter(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', fontSize: '1em', minWidth: '200px' }}
            >
              <option value="All">All Classes</option>
              {classKeys.map(key => (
                <option key={key} value={key}>{classLabelFrom(classLabels, key)}</option>
              ))}
            </select>
	          </div>
	        )}

		        {!loading && classKeys.length > 0 && assessmentCsvExportEnabled && (
		          <AssessmentExportControls
	            user={user}
	            students={students}
	            classes={classes}
	            assessments={yearAssessments}
	            selectedClassFilter={selectedClassFilter}
	            classIds={visibleClassKeys}
	            filterAssessment={filterAssessmentForExport}
	            assessmentTypeLabel={letterAssessmentLabels()}
	            defaultAssessmentCategory="letter_knowledge"
	            filenamePrefix="Letter_Sound_Assessment_Results"
		          />
		        )}

	        {loading ? (
          <p>Loading data...</p>
        ) : loadError ? (
          <LoadErrorNotice error={loadError} onRetry={retryLoad} />
        ) : (
          <>
            {classKeys.length > 0 ? (
	              visibleClassKeys.map((classId, index) => {
                const originalClassId = classLabelFrom(classLabels, classId);
                // The a–z detail grid below is letter tests only: vowel docs
                // (keys like a_long) get their own block so they never leak
                // columns into the letter grid or its Incorrect Count row.
                const classAssessmentsAll = yearAssessments.filter(a => a.classId === classId);
                const classAssessments = classAssessmentsAll.filter(a => !isVowelTestType(a.testType));
                const vowelClassAssessments = classAssessmentsAll.filter(a => isVowelTestType(a.testType));

                const baseOrder = (user?.profile?.useCustomOrder && user?.profile?.customLetterOrder)
                  ? user.profile.customLetterOrder.map(l => l.toLowerCase())
                  : 'abcdefghijklmnopqrstuvwxyz'.split('');

                const letterColumns = [
                  ...new Set(classAssessments.flatMap(a =>
                    a.responses ? Object.keys(a.responses).map(key => key.toLowerCase()) : []
                  ))
                ].sort((a, b) => {
                  const idxA = baseOrder.indexOf(a);
                  const idxB = baseOrder.indexOf(b);
                  if (idxA === -1 && idxB === -1) return a.localeCompare(b);
                  if (idxA === -1) return 1;
                  if (idxB === -1) return -1;
                  return idxA - idxB;
                });

                const currentTestTypeFilter = testTypeFilters[classId] || 'All';
                const currentQuarterFilter = quarterFilters[classId] || 'All';
                const currentStudentFilter = studentFilters[classId] || 'All';

                const uiFilteredAssessments = classAssessments.filter(a => {
                  const matchTestType = currentTestTypeFilter === 'All' || a.testType === currentTestTypeFilter;
                  const matchQuarter = currentQuarterFilter === 'All' || a.quarter === currentQuarterFilter;
                  const matchStudent = currentStudentFilter === 'All' || a.studentId === currentStudentFilter;
                  const hasResults = a.correctCount !== -1 && a.correctCount !== 'N/A';
                  return matchTestType && matchQuarter && matchStudent && hasResults;
                });
                // Removed/archived students are hidden by default so the
                // Incorrect Count row answers "how many CURRENT students
                // missed this letter?" — the checkbox reveals their rows.
                const removedCount = uiFilteredAssessments.filter(a => !rosterIds.has(a.studentId)).length;
                const showRemoved = !!removedShown[classId];
                const rosterFilteredAssessments = showRemoved
                  ? uiFilteredAssessments
                  : uiFilteredAssessments.filter(a => rosterIds.has(a.studentId));
                // Duplicate attempts (same student/test/quarter assessed more
                // than once) collapse to the latest by default — the pick the
                // summary table already makes — so a re-assessed student
                // doesn't double-count in the Incorrect Count row.
                const showAllAttempts = !!allAttemptsShown[classId];
                const olderAttemptCount = rosterFilteredAssessments.filter(a => !latestAttemptIds.has(a.id)).length;
                const filteredClassAssessments = showAllAttempts
                  ? rosterFilteredAssessments
                  : rosterFilteredAssessments.filter(a => latestAttemptIds.has(a.id));

                const incorrectCounts = {};
                letterColumns.forEach(letter => (incorrectCounts[letter] = 0));

                filteredClassAssessments.forEach(assessment => {
                  if (assessment.responses) {
                    for (const [originalKey, result] of Object.entries(assessment.responses)) {
                      if (result === 'Incorrect') {
                        const lowerKey = originalKey.toLowerCase();
                        if (incorrectCounts.hasOwnProperty(lowerKey)) {
                          incorrectCounts[lowerKey]++;
                        }
                      }
                    }
                  }
                });

                const testTypeOrder = { uppercase: 1, lowercase: 2, sounds: 3 };
                const getStudentName = (studentId) => {
                  const student = students.find(s => s.id === studentId);
                  if (student) return student.name;
                  // Archived doc still exists → real name. Hard-deleted → no
                  // doc anywhere (assessments store only studentId), so the
                  // best label possible is "Removed student".
                  const archivedName = studentNamesById[studentId];
                  return archivedName ? `${archivedName} (removed)` : 'Removed student';
                };

                const sortedClassAssessments = [...filteredClassAssessments].sort((a, b) => {
                  const nameA = getStudentName(a.studentId);
                  const nameB = getStudentName(b.studentId);
                  
                  // Sort by last name
                  const aNameParts = nameA.split(/\s+/);
                  const bNameParts = nameB.split(/\s+/);
                  const aLast = (aNameParts[aNameParts.length - 1] || '').toLowerCase();
                  const bLast = (bNameParts[bNameParts.length - 1] || '').toLowerCase();
                  const lastCompare = aLast.localeCompare(bLast);
                  if (lastCompare !== 0) return lastCompare;
                  
                  const aFirst = (aNameParts[0] || '').toLowerCase();
                  const bFirst = (bNameParts[0] || '').toLowerCase();
                  const firstCompare = aFirst.localeCompare(bFirst);
                  if (firstCompare !== 0) return firstCompare;

                  if (a.quarter < b.quarter) return -1;
                  if (a.quarter > b.quarter) return 1;
                  const orderA = testTypeOrder[a.testType] || 99;
                  const orderB = testTypeOrder[b.testType] || 99;
                  return orderA - orderB;
                });

                const formatDate = (date, isManual) => {
                  if (isManual) return 'Manual Entry';
                  if (date instanceof Date) {
                    return date.toLocaleDateString();
                  }
                  return '';
                };

                // Vowel detail rows — the same filter pipeline as the letter
                // grid (test / quarter / student, N/A hidden, removed students
                // and older attempts behind their checkboxes) driven by the
                // vowel block's OWN three selects (PR #111), on the vowel docs
                // only. Empty unless this class gave a vowel test.
                const currentVowelTestTypeFilter = vowelTestTypeFilters[classId] || 'All';
                const currentVowelQuarterFilter = vowelQuarterFilters[classId] || 'All';
                const currentVowelStudentFilter = vowelStudentFilters[classId] || 'All';
                const vowelRows = vowelClassAssessments
                  .filter(a => {
                    const matchTestType = currentVowelTestTypeFilter === 'All' || a.testType === currentVowelTestTypeFilter;
                    const matchQuarter = currentVowelQuarterFilter === 'All' || a.quarter === currentVowelQuarterFilter;
                    const matchStudent = currentVowelStudentFilter === 'All' || a.studentId === currentVowelStudentFilter;
                    const hasResults = a.correctCount !== -1 && a.correctCount !== 'N/A';
                    return matchTestType && matchQuarter && matchStudent && hasResults;
                  })
                  .filter(a => showRemoved || rosterIds.has(a.studentId))
                  .filter(a => showAllAttempts || latestAttemptIds.has(a.id))
                  .sort((a, b) => {
                    const nameCompare = lastNameComparator(
                      { name: getStudentName(a.studentId) }, { name: getStudentName(b.studentId) }
                    );
                    if (nameCompare !== 0) return nameCompare;
                    if (a.quarter < b.quarter) return -1;
                    if (a.quarter > b.quarter) return 1;
                    return enabledVowels.indexOf(a.testType) - enabledVowels.indexOf(b.testType);
                  });
                const vowelColumns = ['a', 'e', 'i', 'o', 'u'];
                const vowelResponseFor = (assessment, testType, vowel) => {
                  const suffix = testType === 'long_vowels' ? 'long' : 'short';
                  return (assessment.responses || {})[`${vowel}_${suffix}`];
                };
                const vowelIncorrectCounts = {};
                vowelColumns.forEach(v => { vowelIncorrectCounts[v] = 0; });
                vowelRows.forEach(a => {
                  vowelColumns.forEach(v => {
                    if (vowelResponseFor(a, a.testType, v) === 'Incorrect') vowelIncorrectCounts[v] += 1;
                  });
                });

                return (
                  <React.Fragment key={classId}>
                    <div className="table-container results-table-container" data-tour={index === 0 ? TOUR_TARGETS.resultsFirstTable : undefined}>
                      <div className="print-header">
                        <strong>Teacher:</strong> {user.displayName || user.email.split('@')[0]} | <strong>Date:</strong> {new Date().toLocaleDateString()} | <strong>Class:</strong> {originalClassId}
                      </div>
                      <h3>Class: {originalClassId}</h3>
                      <div style={{ overflowX: 'auto', border: '1px solid #888' }}>
                        <table className="results-summary-table">
                          <thead>
                            <tr>
                              <th rowSpan="2" className="sticky-col">Student</th>
                              {uniqueYears.map(year => (<th colSpan="4" key={year} className="header-uc section-divider section-start">Uppercase</th>))}
                              {uniqueYears.map(year => (<th colSpan="4" key={`${year}-lowercase`} className="header-lc section-divider">Lowercase</th>))}
                              {uniqueYears.map(year => (<th colSpan="4" key={`${year}-sounds`} className="header-sounds section-divider">Sounds</th>))}
                              {/* Optional vowel tests (v3.73.0) — last in the row, same Q1–Q4 split. */}
                              {enabledVowels.map(vt => uniqueYears.map(year => (
                                <th colSpan="4" key={`${year}-${vt}`} className={`header-${vt.replace('_', '-')} section-divider`}>{VOWEL_TEST_SHORT_LABELS[vt]}</th>
                              )))}
                            </tr>
                            <tr>
                              {uniqueYears.map(year => (<React.Fragment key={`${year}-ucq`}><th className="section-start">Q1</th><th>Q2</th><th>Q3</th><th className="section-divider">Q4</th></React.Fragment>))}
                              {uniqueYears.map(year => (<React.Fragment key={`${year}-lcq`}><th>Q1</th><th>Q2</th><th>Q3</th><th className="section-divider">Q4</th></React.Fragment>))}
                              {uniqueYears.map(year => (<React.Fragment key={`${year}-sq`}><th>Q1</th><th>Q2</th><th>Q3</th><th className="section-divider">Q4</th></React.Fragment>))}
                              {enabledVowels.map(vt => uniqueYears.map(year => (
                                <React.Fragment key={`${year}-${vt}-q`}><th>Q1</th><th>Q2</th><th>Q3</th><th className="section-divider">Q4</th></React.Fragment>
                              )))}
                            </tr>
                          </thead>
                          <tbody>
                            {classSummaries[classId].map(studentSummary => {
                              return (<tr key={studentSummary.studentId} className="student-row">
                              <td className="sticky-col section-divider">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span
                                    onClick={() => {
                                      const studentAssessments = yearAssessments.filter(a => a.studentId === studentSummary.studentId);
                                      setModalData({ student: studentSummary, assessments: studentAssessments });
                                      setSelectedStudent(true);
                                    }}
                                    style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                                  >
                                    {studentSummary.name}
                                  </span>
                                  <button
                                    onClick={() => navigate('/assessment', { state: { studentId: studentSummary.studentId, classId: studentSummary.classId || classId } })}
                                    style={{ padding: '2px 7px', fontSize: '0.72em', cursor: 'pointer', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                  >
                                    Assess
                                  </button>
                                  {canUseNarratives && (
                                    <button
                                      onClick={() => setNarrativeStudent({ studentId: studentSummary.studentId, name: studentSummary.name, classId: studentSummary.classId || classId })}
                                      title="Generate an AI report card narrative draft"
                                      style={{ padding: '2px 7px', fontSize: '0.72em', cursor: 'pointer', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                      📝 Report Card
                                    </button>
                                  )}
                                </div>
                              </td>
                              {uniqueYears.map(year => (<React.Fragment key={`${year}-ucd`}>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'uppercase', year, 'Q1', studentSummary[`uppercase${year}Q1`])} className={`score-cell section-start ${getScoreColorClass(studentSummary[`uppercase${year}Q1`], studentSummary[`uppercase${year}Q1_total`])}`} title={`Test Date: ${formatDate(studentSummary[`uppercase${year}Q1_date`], studentSummary[`uppercase${year}Q1_isManual`])}`}>{letterCellContent(studentSummary, `uppercase${year}Q1`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'uppercase', year, 'Q2', studentSummary[`uppercase${year}Q2`])} className={`score-cell ${getScoreColorClass(studentSummary[`uppercase${year}Q2`], studentSummary[`uppercase${year}Q2_total`])}`} title={`Test Date: ${formatDate(studentSummary[`uppercase${year}Q2_date`], studentSummary[`uppercase${year}Q2_isManual`])}`}>{letterCellContent(studentSummary, `uppercase${year}Q2`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'uppercase', year, 'Q3', studentSummary[`uppercase${year}Q3`])} className={`score-cell ${getScoreColorClass(studentSummary[`uppercase${year}Q3`], studentSummary[`uppercase${year}Q3_total`])}`} title={`Test Date: ${formatDate(studentSummary[`uppercase${year}Q3_date`], studentSummary[`uppercase${year}Q3_isManual`])}`}>{letterCellContent(studentSummary, `uppercase${year}Q3`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'uppercase', year, 'Q4', studentSummary[`uppercase${year}Q4`])} className={`score-cell section-divider ${getScoreColorClass(studentSummary[`uppercase${year}Q4`], studentSummary[`uppercase${year}Q4_total`])}`} title={`Test Date: ${formatDate(studentSummary[`uppercase${year}Q4_date`], studentSummary[`uppercase${year}Q4_isManual`])}`}>{letterCellContent(studentSummary, `uppercase${year}Q4`)}</td>
                              </React.Fragment>))}
                              {uniqueYears.map(year => (<React.Fragment key={`${year}-lcd`}>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'lowercase', year, 'Q1', studentSummary[`lowercase${year}Q1`])} className={`score-cell ${getScoreColorClass(studentSummary[`lowercase${year}Q1`], studentSummary[`lowercase${year}Q1_total`])}`} title={`Test Date: ${formatDate(studentSummary[`lowercase${year}Q1_date`], studentSummary[`lowercase${year}Q1_isManual`])}`}>{letterCellContent(studentSummary, `lowercase${year}Q1`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'lowercase', year, 'Q2', studentSummary[`lowercase${year}Q2`])} className={`score-cell ${getScoreColorClass(studentSummary[`lowercase${year}Q2`], studentSummary[`lowercase${year}Q2_total`])}`} title={`Test Date: ${formatDate(studentSummary[`lowercase${year}Q2_date`], studentSummary[`lowercase${year}Q2_isManual`])}`}>{letterCellContent(studentSummary, `lowercase${year}Q2`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'lowercase', year, 'Q3', studentSummary[`lowercase${year}Q3`])} className={`score-cell ${getScoreColorClass(studentSummary[`lowercase${year}Q3`], studentSummary[`lowercase${year}Q3_total`])}`} title={`Test Date: ${formatDate(studentSummary[`lowercase${year}Q3_date`], studentSummary[`lowercase${year}Q3_isManual`])}`}>{letterCellContent(studentSummary, `lowercase${year}Q3`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'lowercase', year, 'Q4', studentSummary[`lowercase${year}Q4`])} className={`score-cell section-divider ${getScoreColorClass(studentSummary[`lowercase${year}Q4`], studentSummary[`lowercase${year}Q4_total`])}`} title={`Test Date: ${formatDate(studentSummary[`lowercase${year}Q4_date`], studentSummary[`lowercase${year}Q4_isManual`])}`}>{letterCellContent(studentSummary, `lowercase${year}Q4`)}</td>
                              </React.Fragment>))}
                              {uniqueYears.map(year => (<React.Fragment key={`${year}-sd`}>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'sounds', year, 'Q1', studentSummary[`sounds${year}Q1`])} className={`score-cell ${getScoreColorClass(studentSummary[`sounds${year}Q1`], studentSummary[`sounds${year}Q1_total`])}`} title={`Test Date: ${formatDate(studentSummary[`sounds${year}Q1_date`], studentSummary[`sounds${year}Q1_isManual`])}`}>{letterCellContent(studentSummary, `sounds${year}Q1`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'sounds', year, 'Q2', studentSummary[`sounds${year}Q2`])} className={`score-cell ${getScoreColorClass(studentSummary[`sounds${year}Q2`], studentSummary[`sounds${year}Q2_total`])}`} title={`Test Date: ${formatDate(studentSummary[`sounds${year}Q2_date`], studentSummary[`sounds${year}Q2_isManual`])}`}>{letterCellContent(studentSummary, `sounds${year}Q2`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'sounds', year, 'Q3', studentSummary[`sounds${year}Q3`])} className={`score-cell ${getScoreColorClass(studentSummary[`sounds${year}Q3`], studentSummary[`sounds${year}Q3_total`])}`} title={`Test Date: ${formatDate(studentSummary[`sounds${year}Q3_date`], studentSummary[`sounds${year}Q3_isManual`])}`}>{letterCellContent(studentSummary, `sounds${year}Q3`)}</td>
                                <td onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, 'sounds', year, 'Q4', studentSummary[`sounds${year}Q4`])} className={`score-cell section-divider ${getScoreColorClass(studentSummary[`sounds${year}Q4`], studentSummary[`sounds${year}Q4_total`])}`} title={`Test Date: ${formatDate(studentSummary[`sounds${year}Q4_date`], studentSummary[`sounds${year}Q4_isManual`])}`}>{letterCellContent(studentSummary, `sounds${year}Q4`)}</td>
                              </React.Fragment>))}
                              {/* Vowel cells: n/5 banded on the 5-point scale; never administered
                                  renders blank exactly like an un-administered Sounds cell; the
                                  same hidden double-click manual entry (0–5 or NA). */}
                              {enabledVowels.map(vt => uniqueYears.map(year => (<React.Fragment key={`${year}-${vt}-d`}>
                                {QUARTER_KEYS.map((q, qi) => {
                                  const cellKey = `${vt}${year}${q}`;
                                  return (
                                    <td
                                      key={cellKey}
                                      onDoubleClick={() => handleManualScore(studentSummary.studentId, studentSummary.classId || classId, vt, year, q, studentSummary[cellKey])}
                                      className={`score-cell ${qi === 3 ? 'section-divider ' : ''}${vowelScoreColorClass(studentSummary[cellKey])}`}
                                      title={`Test Date: ${formatDate(studentSummary[`${cellKey}_date`], studentSummary[`${cellKey}_isManual`])}`}
                                    >
                                      {displayScore(studentSummary[cellKey])}
                                    </td>
                                  );
                                })}
                              </React.Fragment>)))}
                            </tr>);})}
                          </tbody>
                        </table>
                      </div>
                      <br />
                      <div className="second-table">
                        <h3>Detailed Letter Responses for {originalClassId}</h3>
                        {(removedCount > 0 || olderAttemptCount > 0) && (
                          <div className="no-print" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginBottom: '8px' }}>
                            {removedCount > 0 && (
                              <label className="checkbox-row" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.95em', color: '#555', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={showRemoved}
                                  onChange={(e) => setRemovedShown(prev => ({ ...prev, [classId]: e.target.checked }))}
                                />
                                Show {removedCount} result{removedCount === 1 ? '' : 's'} from students no longer on this roster
                              </label>
                            )}
                            {olderAttemptCount > 0 && (
                              <label className="checkbox-row" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.95em', color: '#555', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={showAllAttempts}
                                  onChange={(e) => setAllAttemptsShown(prev => ({ ...prev, [classId]: e.target.checked }))}
                                />
                                Show all attempts ({olderAttemptCount} older attempt{olderAttemptCount === 1 ? '' : 's'})
                              </label>
                            )}
                          </div>
                        )}
                        <div style={{ maxHeight: '600px', overflowY: 'auto', border: '1px solid #888' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
                              <tr style={{ backgroundColor: '#f4f4f4' }}>
                                <th style={{ border: '1px solid #888', padding: '10px', minWidth: '150px', whiteSpace: 'nowrap' }}>
                                  <select
                                    value={studentFilters[classId] || 'All'}
                                    onChange={(e) => setStudentFilters(prev => ({ ...prev, [classId]: e.target.value }))}
                                    style={{
                                      padding: '4px',
                                      borderRadius: '4px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      fontWeight: 'bold',
                                      fontSize: '1em',
                                      width: '100%',
                                      cursor: 'pointer',
                                      appearance: 'auto'
                                    }}
                                  >
                                    <option value="All">Student (All)</option>
                                    {classSummaries[classId]?.map(student => (
                                      <option key={student.studentId} value={student.studentId}>{student.name}</option>
                                    ))}
                                  </select>
                                </th>
                                <th style={{ border: '1px solid #888', padding: '10px', minWidth: '125px' }}>
                                  <select
                                    value={testTypeFilters[classId] || 'All'}
                                    onChange={(e) => setTestTypeFilters(prev => ({ ...prev, [classId]: e.target.value }))}
                                    style={{
                                      padding: '4px',
                                      borderRadius: '4px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      fontWeight: 'bold',
                                      fontSize: '1em',
                                      width: '100%',
                                      cursor: 'pointer',
                                      appearance: 'auto'
                                    }}
                                  >
                                    <option value="All">Test Type (All)</option>
                                    <option value="uppercase">Uppercase</option>
                                    <option value="lowercase">Lowercase</option>
                                    <option value="sounds">Sounds</option>
                                  </select>
                                </th>
                                <th style={{ border: '1px solid #888', padding: '10px', minWidth: '125px' }}>
                                  <select
                                    value={quarterFilters[classId] || 'All'}
                                    onChange={(e) => setQuarterFilters(prev => ({ ...prev, [classId]: e.target.value }))}
                                    style={{
                                      padding: '4px',
                                      borderRadius: '4px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      fontWeight: 'bold',
                                      fontSize: '1em',
                                      width: '100%',
                                      cursor: 'pointer',
                                      appearance: 'auto'
                                    }}
                                  >
                                    <option value="All">Quarter (All)</option>
                                    {uniqueYears.map(year => (
                                      <React.Fragment key={year}>
                                        <option value={`${year}Q1`}>{year} Q1</option>
                                        <option value={`${year}Q2`}>{year} Q2</option>
                                        <option value={`${year}Q3`}>{year} Q3</option>
                                        <option value={`${year}Q4`}>{year} Q4</option>
                                      </React.Fragment>
                                    ))}
                                  </select>
                                </th>
                                <th style={{ border: '1px solid #888', padding: '10px' }}>Correct</th>
                                {letterColumns.map(letter => (<th key={letter} style={{ border: '1px solid #888', padding: '10px' }}>{formatLetterForAssessment(letterCaseForSelection(currentTestTypeFilter), letter)}</th>))}
                              </tr>
                              <tr style={{ backgroundColor: '#fffacd', fontWeight: 'bold' }}>
                                <th colSpan="4" style={{ border: '1px solid #888', padding: '10px', textAlign: 'right' }}>Incorrect Count:</th>
                                {letterColumns.map(letter => (<th key={`${letter}-incorrect`} style={{ border: '1px solid #888', padding: '10px', textAlign: 'center' }}>{incorrectCounts[letter]}</th>))}
                              </tr>
                            </thead>
                            <tbody>
                              {sortedClassAssessments.length > 0 ? (
                                sortedClassAssessments.map(assessment => {
                                  const onRoster = rosterIds.has(assessment.studentId);
                                  const responses = assessment.responses || {};
                                  // v3.85.0: letters the run preset as not administered (a consonants-only
                                  // Sounds run: a e i o u) render grey "n/a", never blank, never red.
                                  const notAdministeredLetters = new Set((Array.isArray(assessment.notAdministered) ? assessment.notAdministered : []).map(l => String(l).toLowerCase()));
                                  const saidLetterCase = assessment.testType === 'sounds' && LETTER_CASES.includes(assessment.letterCase) ? letterCaseOf(assessment) : null;
                                  return (
                                    <tr key={assessment.id}>
                                      <td style={{ border: '1px solid #888', padding: '10px', fontStyle: onRoster ? 'normal' : 'italic', color: onRoster ? 'inherit' : '#777' }}>{getStudentName(assessment.studentId)}</td>
                                      <td style={{ border: '1px solid #888', padding: '10px' }}>
                                        {assessment.testType.charAt(0).toUpperCase() + assessment.testType.slice(1)}
                                        {hasLetterCase(assessment) && (
                                          <span className="case-tag" title={caseTagTitle(assessment.letterCase)} style={CASE_TAG_STYLE}>{caseTagText(assessment.letterCase)}</span>
                                        )}
                                      </td>
                                      <td style={{ border: '1px solid #888', padding: '10px' }}>
                                        {(() => {
                                          const q = assessment.quarter ? 'Q' + assessment.quarter.split('Q')[1] : assessment.quarter;
                                          const d = assessment.timestamp ? new Date(assessment.timestamp).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : null;
                                          return d ? `${q} - ${d}` : q;
                                        })()}
                                      </td>
                                      <td style={{ border: '1px solid #888', padding: '10px', textAlign: 'center', backgroundColor: (assessment.correctCount === -1 || assessment.correctCount === 'N/A') ? '#f0f0f0' : 'transparent', color: (assessment.correctCount === -1 || assessment.correctCount === 'N/A') ? '#aaa' : 'inherit' }}>
                                        {(assessment.correctCount === -1 || assessment.correctCount === 'N/A')
                                          ? 'NA'
                                          : (Number(assessment.totalLetters) > 0 && Number(assessment.totalLetters) !== LETTER_MAX)
                                            ? `${assessment.correctCount}/${assessment.totalLetters}`
                                            : assessment.correctCount}
                                      </td>
                                      {letterColumns.map(letter => {
                                        const upperCaseLetter = letter.toUpperCase();
                                        const response = responses[letter] || responses[upperCaseLetter];
                                        const isNA = assessment.correctCount === -1 || assessment.correctCount === 'N/A';

                                        let finalResponse = response;
                                        if (!finalResponse && assessment.manualEntry && assessment.correctCount === 0) {
                                          finalResponse = 'Incorrect';
                                        }

                                        // Hover detail (v3.27.0): correct cells show response time;
                                        // incorrect cells add the student's captured response when the
                                        // teacher recorded one via a letter key. Both maps are keyed by
                                        // the assessed letter as displayed, so mirror the responses[]
                                        // lowercase/uppercase lookup above.
                                        const fluency = assessment.letterFluency || {};
                                        const heardMap = assessment.incorrectResponses || {};
                                        const fluencyMs = fluency[letter] !== undefined ? fluency[letter] : fluency[upperCaseLetter];
                                        const heardLetter = heardMap[letter] !== undefined ? heardMap[letter] : heardMap[upperCaseLetter];
                                        const notAdministered = !isNA && notAdministeredLetters.has(letter);
                                        const cellTitle = isNA ? undefined : notAdministered ? 'Not administered' : buildResponseCellTooltip(finalResponse, heardLetter, fluencyMs);
                                        // The said letter shows inside the red cell (SAID_LETTER_STYLE), in the
                                        // case the run was given in (a lowercase Sounds run → lowercase).
                                        const saidLetterRaw = !isNA && finalResponse === 'Incorrect' && heardLetter ? heardLetter : null;
                                        const saidLetter = saidLetterRaw && saidLetterCase === 'lowercase' ? String(saidLetterRaw).toLowerCase() : saidLetterRaw;

                                        return (
                                          <td
                                            key={letter}
                                            title={cellTitle}
                                            style={{
                                              border: '1px solid #888', padding: '10px',
                                              backgroundColor: isNA ? '#f0f0f0' : (finalResponse === 'Correct' ? '#90ee90' : finalResponse === 'Incorrect' ? '#ff6347' : 'transparent'),
                                              width: '50px', textAlign: 'center',
                                              cursor: cellTitle ? 'help' : 'default',
                                              ...(notAdministered ? NOT_ADMINISTERED_CELL_STYLE : {})
                                            }}
                                          >
                                            {notAdministered
                                              ? 'n/a'
                                              : saidLetter && <span className="said-letter" style={SAID_LETTER_STYLE}>{saidLetter}</span>}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr><td colSpan={letterColumns.length + 4} style={{ border: '1px solid #888', padding: '10px', textAlign: 'center' }}>No detailed assessments available for this class.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Detailed Vowel Responses (v3.73.0): one block per class, only
                          when the class has given a vowel test (4K classes that never
                          do simply don't get this block). Lists the five vowels with
                          the result state; not administered is its own gray state,
                          never a red mark. Filters itself — Student / Test / Quarter
                          selects of its own, independent of the grid above (PR #111). */}
                      {enabledVowels.length > 0 && vowelClassAssessments.length > 0 && (
                        <div className="second-table vowel-detail-table" style={{ marginTop: '18px' }}>
                          <h3>Detailed Vowel Responses for {originalClassId}</h3>
                          <div style={{ overflowX: 'auto', border: '1px solid #888' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f4f4f4' }}>
                                  <th style={{ border: '1px solid #888', padding: '10px', minWidth: '150px', whiteSpace: 'nowrap' }}>
                                    <select
                                      aria-label="Filter vowel responses by student"
                                      value={vowelStudentFilters[classId] || 'All'}
                                      onChange={(e) => setVowelStudentFilters(prev => ({ ...prev, [classId]: e.target.value }))}
                                      style={DETAIL_FILTER_SELECT_STYLE}
                                    >
                                      <option value="All">Student (All)</option>
                                      {classSummaries[classId]?.map(student => (
                                        <option key={student.studentId} value={student.studentId}>{student.name}</option>
                                      ))}
                                    </select>
                                  </th>
                                  <th style={{ border: '1px solid #888', padding: '10px', minWidth: '125px' }}>
                                    <select
                                      aria-label="Filter vowel responses by test"
                                      value={vowelTestTypeFilters[classId] || 'All'}
                                      onChange={(e) => setVowelTestTypeFilters(prev => ({ ...prev, [classId]: e.target.value }))}
                                      style={DETAIL_FILTER_SELECT_STYLE}
                                    >
                                      <option value="All">Test (All)</option>
                                      {enabledVowels.map(vt => (
                                        <option key={vt} value={vt}>{VOWEL_TEST_LABELS[vt]}</option>
                                      ))}
                                    </select>
                                  </th>
                                  <th style={{ border: '1px solid #888', padding: '10px', minWidth: '125px' }}>
                                    <select
                                      aria-label="Filter vowel responses by quarter"
                                      value={vowelQuarterFilters[classId] || 'All'}
                                      onChange={(e) => setVowelQuarterFilters(prev => ({ ...prev, [classId]: e.target.value }))}
                                      style={DETAIL_FILTER_SELECT_STYLE}
                                    >
                                      <option value="All">Quarter (All)</option>
                                      {uniqueYears.map(year => (
                                        <React.Fragment key={year}>
                                          <option value={`${year}Q1`}>{year} Q1</option>
                                          <option value={`${year}Q2`}>{year} Q2</option>
                                          <option value={`${year}Q3`}>{year} Q3</option>
                                          <option value={`${year}Q4`}>{year} Q4</option>
                                        </React.Fragment>
                                      ))}
                                    </select>
                                  </th>
                                  <th style={{ border: '1px solid #888', padding: '10px' }}>Correct</th>
                                  {vowelColumns.map(v => (<th key={v} style={{ border: '1px solid #888', padding: '10px' }}>{v}</th>))}
                                </tr>
                                <tr style={{ backgroundColor: '#fffacd', fontWeight: 'bold' }}>
                                  <th colSpan="4" style={{ border: '1px solid #888', padding: '10px', textAlign: 'right' }}>Incorrect Count:</th>
                                  {vowelColumns.map(v => (<th key={`${v}-incorrect`} style={{ border: '1px solid #888', padding: '10px', textAlign: 'center' }}>{vowelIncorrectCounts[v]}</th>))}
                                </tr>
                              </thead>
                              <tbody>
                                {vowelRows.length > 0 ? vowelRows.map(assessment => {
                                  const onRoster = rosterIds.has(assessment.studentId);
                                  return (
                                    <tr key={assessment.id}>
                                      <td style={{ border: '1px solid #888', padding: '10px', fontStyle: onRoster ? 'normal' : 'italic', color: onRoster ? 'inherit' : '#777' }}>{getStudentName(assessment.studentId)}</td>
                                      <td style={{ border: '1px solid #888', padding: '10px' }}>{VOWEL_TEST_LABELS[assessment.testType]}</td>
                                      <td style={{ border: '1px solid #888', padding: '10px' }}>
                                        {(() => {
                                          const q = assessment.quarter ? 'Q' + assessment.quarter.split('Q')[1] : assessment.quarter;
                                          const d = assessment.timestamp ? new Date(assessment.timestamp).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : null;
                                          return d ? `${q} - ${d}` : q;
                                        })()}
                                      </td>
                                      <td style={{ border: '1px solid #888', padding: '10px', textAlign: 'center' }}>{assessment.correctCount}/{VOWEL_ITEMS_PER_TEST}</td>
                                      {vowelColumns.map(v => {
                                        const response = vowelResponseFor(assessment, assessment.testType, v);
                                        const notAdministered = response === RESP_NOT_ADMINISTERED;
                                        // Same hover detail as the letter grid: response time, plus
                                        // the letter the student said when the teacher captured one
                                        // via the letter key (incorrectResponses, keyed by item id).
                                        const itemId = vowelItemIds(assessment.testType)[vowelColumns.indexOf(v)];
                                        const fluencyMs = (assessment.letterFluency || {})[itemId];
                                        const heardLetter = (assessment.incorrectResponses || {})[itemId];
                                        const cellTitle = notAdministered
                                          ? 'Not administered'
                                          : buildResponseCellTooltip(response, heardLetter, fluencyMs);
                                        return (
                                          <td
                                            key={v}
                                            title={cellTitle}
                                            style={{
                                              border: '1px solid #888', padding: '10px', width: '50px', textAlign: 'center',
                                              backgroundColor: response === 'Correct' ? '#90ee90' : response === 'Incorrect' ? '#ff6347' : notAdministered ? '#e5e7eb' : 'transparent',
                                              color: notAdministered ? '#6b7280' : 'inherit', fontSize: notAdministered ? '0.8em' : 'inherit',
                                              cursor: cellTitle ? 'help' : 'default'
                                            }}
                                          >
                                            {notAdministered
                                              ? 'n/a'
                                              : (response === 'Incorrect' && heardLetter)
                                                ? <span className="said-letter" style={SAID_LETTER_STYLE}>{heardLetter}</span>
                                                : ''}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                }) : (
                                  <tr><td colSpan={vowelColumns.length + 4} style={{ border: '1px solid #888', padding: '10px', textAlign: 'center' }}>No vowel assessments match the current filters.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>

                    {classKeys.length > 1 && index < classKeys.length - 1 && (
                      <hr className="separator-line" style={{ margin: '40px 0', border: 'none', borderTop: '4px solid #bbb' }} />
                    )}

                  </React.Fragment>
                );
              })
            ) : (
              <NoRosterNotice pageLabel="the Results page" />
            )}

            {/* Hidden behind the savedReportCards flag per Kemal (2026-07-14)
                — component and wiring kept intact for later re-enable. */}
            {isFeatureEnabled('savedReportCards') && canUseNarratives && classKeys.length > 0 && (
              <SavedReportCardsSection
                expanded={savedCardsExpanded}
                onToggle={() => setSavedCardsExpanded((v) => !v)}
                reportType={ovReportType}
                setReportType={setOvReportType}
                quarterNumber={ovQuarter}
                setQuarterNumber={setOvQuarter}
                semester={ovSemester}
                setSemester={setOvSemester}
                schoolYear={ovSchoolYear}
                setSchoolYear={setOvSchoolYear}
                classGroups={overviewClassGroups}
                draftsByStudent={draftsByStudent}
                loading={draftsLoading}
                onView={handleOverviewView}
                onPrint={handleOverviewPrint}
                onGenerate={handleOverviewGenerate}
              />
            )}
          </>
        )}
      </div>
      {selectedStudent && (
        <StudentDetailsModal
          user={user}
          readinessNote={readinessNotesByStudent[modalData.student.studentId] || ''}
          student={modalData.student}
          assessments={yearAssessments.filter(a => a.studentId === modalData.student.studentId)}
          letterColumns={(user?.profile?.useCustomOrder && user?.profile?.customLetterOrder) 
            ? user.profile.customLetterOrder.map(l => l.toLowerCase())
            : 'abcdefghijklmnopqrstuvwxyz'.split('')
          }
          onClose={() => setSelectedStudent(false)}
          onRefresh={retryLoad}
          academicYearLabel={selectedSchoolYear != null ? schoolYearLabel(selectedSchoolYear) : ''}
        />
      )}
      {canUseNarratives && narrativeStudent && (
        <ReportCardNarrativeModal
          user={user}
          student={narrativeStudent}
          initialReportType={narrativeStudent.initial?.reportType}
          initialQuarterNumber={narrativeStudent.initial?.quarterNumber}
          initialSemester={narrativeStudent.initial?.semester}
          initialSchoolYear={narrativeStudent.initial?.schoolYear}
          autoOpenDraftId={narrativeStudent.autoOpenDraftId}
          onClose={() => {
            const closedId = narrativeStudent?.studentId;
            setNarrativeStudent(null);
            // Refresh this student's drafts so the overview reflects any new
            // generation / save without a full page reload.
            if (closedId) loadDraftsForStudents([closedId], { force: true });
          }}
        />
      )}
      {printDraft && (
        <ReportCardPrintView
          studentName={printDraft.studentName}
          studentClass={printDraft.studentClass}
          schoolYear={printDraft.schoolYear}
          periodLabel={printDraft.periodLabel}
          narrativeText={printDraft.narrativeText}
          onClose={() => setPrintDraft(null)}
        />
      )}
    </div>
  );
}

export default Results;
